// Generated by TaroBase SDK Generator: 2026-05-01T17:36:41.366Z
// Collection: sessions/$sessionId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Checkout sessions for a tenant. Service-only writes; tenant owner reads via backend allowlist; payer access goes through a backend allowlist route, NOT direct read.
 */

export interface SessionsRequest {
  appid: string;
  tenantUserId: string;
  amountUsdCents: number | TimeOperation | IncrementOperation | TokenAmount;
  accepts: string;
  chosenRail?: string;
  kind?: string;
  metadata?: string;
  solanaPayReference?: AddressType;
  paymentAccountId?: string;
  paymentAddress?: AddressType;
  whopCheckoutUrl?: string;
  whopOrderId?: string;
  recipientWallet: AddressType;
  productWebhook?: string;
  successRedirectUrl?: string;
  status: string;
  txSignature?: string;
  sweepSignature?: string;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
  paidAt?: number | TimeOperation | IncrementOperation | TokenAmount;
  settledAt?: number | TimeOperation | IncrementOperation | TokenAmount;
  expiresAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface SessionsResponse {
  appid: string;
  tenantUserId: string;
  amountUsdCents: number;
  accepts: string;
  chosenRail?: string;
  kind?: string;
  metadata?: string;
  solanaPayReference?: string;
  paymentAccountId?: string;
  paymentAddress?: string;
  whopCheckoutUrl?: string;
  whopOrderId?: string;
  recipientWallet: string;
  productWebhook?: string;
  successRedirectUrl?: string;
  status: string;
  txSignature?: string;
  sweepSignature?: string;
  createdAt: number;
  paidAt?: number;
  settledAt?: number;
  expiresAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a Sessions operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildSessions(sessionId: string, data: SessionsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `sessions/${sessionId}`, document: doc };
}

/**
 * Service-only. Backend creates the session after validating tenant API key, sets appid/tenantUserId/amountUsdCents/accepts/recipientWallet/status='pending'/createdAt/expiresAt. paymentAccountId stores the internal pp_<hash> AccountPlugin id (used server-side and as the recipient in paymentDeposits passthrough writes), while paymentAddress stores the resolved base58 PDA public key (returned by paymentAccounts.getPdaAddress) and is what's shown in checkout UIs / Solana Pay QR codes. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setSessions(sessionId: string, data: SessionsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`sessions/${sessionId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting Sessions: ${formatError(error)}`);
    return false;
  }
}

export type SessionsRequestUpdate = Partial<SessionsRequest>;

/**
 * Build a Sessions update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateSessions(sessionId: string, data: SessionsRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `sessions/${sessionId}`, document: doc };
}

/**
 * Service-only. Backend transitions status (pending → rail_chosen → payment_detected → settled / failed / expired) and fills chosenRail, txSignature, sweepSignature, paidAt, settledAt as the session progresses. When a rail is selected/quoted the backend ALSO updates the quote-lock fields (quotedRail, quotedMint, quotedNativeAmount, quoteExpiresAt, amountToleranceBps) and writes a parallel onchain sessionQuotes/<sessionId> doc — the onchain mirror is what the paymentDeposits.create rule enforces against. quotedNativeAmount is String here for big-int safety; the onchain mirror stores it as UInt. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateSessions(sessionId: string, data: SessionsRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`sessions/${sessionId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating Sessions: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Service vault and admin always read. Tenant owner reads sessions for their appid (resolved via tenants/$appid.owner). Public payer access is NOT allowed directly here — frontend uses a backend allowlist route that returns sanitized session info.
   (Get Single Item)
 */
export async function getSessions(sessionId: string): Promise<SessionsResponse | null> {
  try {
    const result = await get(`sessions/${sessionId}`);
    return result as SessionsResponse | null;
  } catch (error) {
    console.error(`Error getting Sessions: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single Sessions document. (
  Read Operation Details: Service vault and admin always read. Tenant owner reads sessions for their appid (resolved via tenants/$appid.owner). Public payer access is NOT allowed directly here — frontend uses a backend allowlist route that returns sanitized session info.
  )
 */
export function subscribeSessions(callback: (data: SessionsResponse | null) => void, sessionId: string): Promise<() => Promise<void>> {
  return subscribe(`sessions/${sessionId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as SessionsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to Sessions at path '${`sessions/${sessionId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many Sessions items from collection sessions
 
  Read Operation Details: Service vault and admin always read. Tenant owner reads sessions for their appid (resolved via tenants/$appid.owner). Public payer access is NOT allowed directly here — frontend uses a backend allowlist route that returns sanitized session info.
  
 */
export async function getManySessions(filter: string = ""): Promise<SessionsResponse[]> {
  try {
    const results = await get("sessions", { prompt: filter });
    return Array.isArray(results) ? results as SessionsResponse[] : [];
  } catch (error) {
    console.error(`Error getting Sessions collection at path 'sessions': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Sessions collection at sessions
 
  Read Operation Details: Service vault and admin always read. Tenant owner reads sessions for their appid (resolved via tenants/$appid.owner). Public payer access is NOT allowed directly here — frontend uses a backend allowlist route that returns sanitized session info.
  
 */
export function subscribeManySessions(callback: (data: SessionsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("sessions", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as SessionsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Sessions collection at path '${"sessions"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all Sessions items from collection sessions
 
  Read Operation Details: Service vault and admin always read. Tenant owner reads sessions for their appid (resolved via tenants/$appid.owner). Public payer access is NOT allowed directly here — frontend uses a backend allowlist route that returns sanitized session info.
  
 */
export async function getAllSessions(filter: string = ""): Promise<SessionsResponse[]> {
  try {
    const results = await get("sessions", { prompt: filter });
    return Array.isArray(results) ? results as SessionsResponse[] : [];
  } catch (error) {
    console.error(`Error getting Sessions collection at path 'sessions': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Sessions collection at sessions
 
  Read Operation Details: Service vault and admin always read. Tenant owner reads sessions for their appid (resolved via tenants/$appid.owner). Public payer access is NOT allowed directly here — frontend uses a backend allowlist route that returns sanitized session info.
  
 */
export function subscribeAllSessions(callback: (data: SessionsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("sessions", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as SessionsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Sessions collection at path '${"sessions"}': ${formatError(error)}`);
    }
  });
}

